import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Badge, Spinner } from "../../../components/ui";
import { useVehiclesSocketStore } from "../../../store/admin/vehiclesSocketStore";
import type { MonitoringUnitRow } from "../hooks/useMonitoringCenter";
import type { CentinelaCaseView } from "../services/mapCentinelaCase";
import {
  CASE_URGENCY_TONES,
  WATCH_STATUS_MAP_COLORS,
  WATCH_STATUS_TONES,
} from "../theme";
import FichaTactica from "./FichaTactica";

export interface MonitoringUnitPanelProps {
  row: MonitoringUnitRow;
  activeCase?: CentinelaCaseView | null;
  labelOfCase: (item: CentinelaCaseView) => string;
  casesLoading?: boolean;
  onClose: () => void;
  /** Abre la tarjeta del caso. Se turnan en la misma columna. */
  onAbrirCaso?: (item: CentinelaCaseView, intencion?: "ver" | "resolver") => void;
}

function PanelPending({ label }: { label: string }) {
  return (
    <p className="flex items-center gap-2 border-t border-line pt-3 text-[10.5px] text-content-faint">
      <Spinner size="xs" className="text-primary-500" label={label} />
      {label}
    </p>
  );
}

export default function MonitoringUnitPanel({
  row,
  activeCase = null,
  labelOfCase,
  casesLoading,
  onClose,
  onAbrirCaso,
}: MonitoringUnitPanelProps) {
  const { t } = useTranslation("common");

  const requestAddressForDevice = useVehiclesSocketStore(
    (state) => state.requestAddressForDevice,
  );
  const requestedAddresses = useRef<Set<string>>(new Set());


  useEffect(() => {
    const deviceId = row.deviceId;
    if (!deviceId || requestedAddresses.current.has(deviceId)) return;
    requestedAddresses.current.add(deviceId);
    requestAddressForDevice(deviceId);
  }, [row.deviceId, requestAddressForDevice]);




  const caseView = activeCase ?? row.openCase;

  // Que el caso pida a una persona es lo que decide si esta fila se resalta y
  // si ofrece el atajo a resolver. Un caso que Centinela está atendiendo solo
  // no necesita ninguna de las dos cosas: enseñarlas igual las gastaría.
  const esperando = Boolean(caseView?.requiresHuman && caseView?.isOpen);



  const tone = WATCH_STATUS_MAP_COLORS[row.status];


  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-card">
      <div className="flex shrink-0 items-center gap-2 border-b border-line px-3 py-2">
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: tone }} />
        <span className="truncate font-mono text-[12px] font-bold text-content">
          {row.plate ?? row.name}
        </span>
        <Badge tone={WATCH_STATUS_TONES[row.status]} size="sm">
          {t(`monitoreoIa.status.${row.status}`)}
        </Badge>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("monitoreoIa.monitoringCenter.closePanel")}
          className="ml-auto flex items-center justify-center text-content-faint transition-colors hover:text-content max-lg:h-10 max-lg:w-10"
        >
          <i className="ri-close-line text-sm" />
        </button>
      </div>

      <FichaTactica row={row} />

      <div className="min-h-0 min-w-0 flex-1 space-y-3 overflow-y-auto overflow-x-hidden p-3 max-lg:space-y-4 max-lg:p-4">
        {/* ── Lo que se fue de aquí, y a dónde ──────────────────────────
            La dirección, la velocidad y el «hace N minutos» se mudaron a la
            LISTA de unidades, y el mensaje de Centinela al resumen. Los tres
            se apilaban encima del caso y lo empujaban fuera de la vista: había
            que bajar para encontrar lo único que pedía una decisión.

            Además se repetían. La ubicación es la pregunta que se hace
            recorriendo la flota, no abriendo una unidad —para eso ya está
            seleccionada y visible en el mapa—, y el mensaje de Centinela vive
            completo en el resumen.

            Debajo de la ficha queda el caso, que es lo único que cabe. */}

        {row.chips.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {row.chips.map((chip) => (
              <Badge key={chip.label} tone={chip.tone} size="sm">
                {chip.label}
              </Badge>
            ))}
          </div>
        ) : null}

        {/* Los avisos de «sin teléfono» y «sin custodia» se fueron: son los
            dos chips en ámbar de la ficha, y repetirlos como párrafo largo
            ocupaba media tarjeta para decir lo mismo dos veces. */}

        {/* ── El caso vive en SU tarjeta ────────────────────────────────
            Aquí sólo queda el enlace. Antes la escalera del caso, sus pasos,
            el marcador de robo y la evidencia colgaban debajo de la ficha, y
            eran dos tarjetas apiladas en una: quien abría la unidad para saber
            quién la conduce se encontraba media pantalla de un caso que quizá
            no venía a ver.

            Son dos preguntas distintas —«¿quién va en esta unidad?» y «¿qué
            pasó en este hecho?»— y ahora se turnan en la misma columna. */}
        {caseView ? (
          <div
            /* ── El que te espera se ve, el que no espera nada no estorba ──
               Cuando el caso pide intervención, esta fila deja de ser un
               enlace discreto al pie de la tarjeta: se enmarca en ámbar y se
               anuncia. Al fondo y sin color se perdía entre la ficha del
               conductor y la telemetría, que es justo lo que pasó. */
            className={
              esperando
                ? "rounded-lg border border-warning/50 bg-warning/10 p-2"
                : ""
            }
          >
            {esperando ? (
              <p className="mb-1.5 flex items-center gap-1 font-mono text-[9px] font-semibold uppercase tracking-wider text-warning">
                <i className="ri-user-follow-line" aria-hidden="true" />
                {t("monitoreoIa.monitoringCenter.esperandoAlguien", { defaultValue: "Te toca" })}
              </p>
            ) : null}

            <button
              type="button"
              onClick={() => onAbrirCaso?.(caseView, "ver")}
              className={`flex w-full items-center gap-2 rounded-lg border p-2 text-left transition-colors ${
                esperando ? "border-warning/40 bg-card hover:bg-subtle" : "border-line bg-surface hover:bg-subtle"
              }`}
            >
              <Badge tone={CASE_URGENCY_TONES[caseView.urgency]} size="sm">
                {t(`monitoreoIa.caseEvents.urgency.${caseView.urgency}`)}
              </Badge>
              <span className="min-w-0 flex-1 truncate text-[11px] text-content-secondary">
                {labelOfCase(caseView)}
              </span>
              <i className="ri-arrow-right-s-line shrink-0 text-content-faint" aria-hidden="true" />
            </button>

            {/* ── Los dos atajos ───────────────────────────────────────────
                «Revisar» sale en CUALQUIER caso abierto: la fila de arriba ya
                lleva al caso, pero una fila que parece texto no se toca. Un
                botón con su verbo sí, y es la diferencia entre que alguien
                mire el caso o lo deje pasar.

                «Resolver» sólo cuando el caso espera a una persona, y va
                aparte y no dentro del otro: son dos intenciones distintas
                —mirar qué pasó y darlo por atendido— y meterlas en el mismo
                control haría que quien sólo quería ver acabara en un
                formulario de cierre.

                Ninguno cierra nada al tocarlo. «Resolver» abre el caso con el
                formulario ya desplegado, y la nota sigue siendo obligatoria:
                ésa es la guarda que importa. */}
            {caseView.isOpen ? (
              <div className="mt-1.5 flex gap-1.5 max-lg:mt-2 max-lg:gap-2">
                <button
                  type="button"
                  onClick={() => onAbrirCaso?.(caseView, "ver")}
                  className="flex min-w-0 flex-1 items-center justify-center gap-1 rounded-lg border border-line py-1.5 text-[10.5px] max-lg:min-h-10 max-lg:text-xs font-medium text-content-secondary transition-colors hover:bg-subtle"
                >
                  <i className="ri-search-eye-line" aria-hidden="true" />
                  {t("monitoreoIa.monitoringCenter.revisarCaso", { defaultValue: "Revisar el caso" })}
                </button>

                {esperando ? (
                  <button
                    type="button"
                    onClick={() => onAbrirCaso?.(caseView, "resolver")}
                    className="flex min-w-0 flex-1 items-center justify-center gap-1 rounded-lg border border-warning/40 py-1.5 text-[10.5px] max-lg:min-h-10 max-lg:text-xs font-medium text-warning transition-colors hover:bg-warning/10"
                  >
                    <i className="ri-check-double-line" aria-hidden="true" />
                    {t("monitoreoIa.monitoringCenter.resolverCaso", { defaultValue: "Resolver" })}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : casesLoading ? (
          <PanelPending label={t("monitoreoIa.monitoringCenter.loadingCases", { defaultValue: "Buscando casos…" })} />
        ) : (
          <p className="text-[10.5px] text-content-faint">
            {t("monitoreoIa.monitoringCenter.noOpenCases", { defaultValue: "Sin casos abiertos." })}
          </p>
        )}

      </div>
    </div>
  );
}
